import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import Shell from "@/components/Shell";
import Brandmark from "@/components/Brandmark";
import { setAttribution } from "@/lib/attribution";
import { track } from "@/lib/analytics";

// Handoff for users arriving from the external ScoreApp "Signal State Snapshot".
// Accepts only non-sensitive referral metadata. Never repeats the assessment.
export default function FromScoreApp() {
  const nav = useNavigate();

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    setAttribution({
      source: p.get("source") || "scoreapp",
      campaign: p.get("campaign") || undefined,
      entry_context: "scoreapp_handoff",
    });
    track("snapshot_referral_in", { source: "scoreapp" });
  }, []);

  return (
    <Shell>
      <div className="min-h-[88vh] px-6 pt-20 pb-16 flex flex-col" data-testid="from-scoreapp">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
          <Brandmark size={34} />
        </motion.div>

        <div className="flex-1 flex flex-col justify-center py-6">
          <motion.h1
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="font-serif-display text-[28px] sm:text-3xl leading-[1.25] text-[#1A1A1A]"
          >
            Your Snapshot gave you a starting point.{" "}
            <span className="text-[#8a6d10]">Now let's see what changes over time.</span>
          </motion.h1>
          <p className="mt-5 text-[15px] leading-relaxed text-[#474747]">
            A Snapshot shows you one moment. Signal State ID helps you see the pattern — a quick
            3-question check whenever you want to know the state you're bringing into what's next.
          </p>
          <p className="mt-4 text-[13.5px] text-[#6B6B6B]">
            No need to repeat the assessment. Your next step is simply to check your state today.
          </p>
        </div>

        <motion.div
          className="mt-8"
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}
        >
          <button
            data-testid="scoreapp-cta"
            onClick={() => nav("/navigate")}
            className="w-full inline-flex items-center justify-center gap-2 bg-[#1A1A1A] text-[#FDFBF7] rounded-full px-6 py-4 text-base font-medium ss-tap ss-focus hover:-translate-y-0.5"
          >
            Check my Signal State <ArrowRight size={18} strokeWidth={1.8} />
          </button>
          <p className="mt-4 text-center text-[12.5px] text-[#6B6B6B]">3 questions · about 60 seconds</p>
        </motion.div>
      </div>
    </Shell>
  );
}
